import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib_api';

function cardTitle(label: string, value: string | number, hint?: string) {
  return (
    <article className="panel p-4">
      <p className="text-sm text-mutedGray dark:text-darkTextSecondary">{label}</p>
      <p className="mt-2 text-2xl font-semibold text-darkText dark:text-darkTextPrimary">{value}</p>
      {hint ? <p className="mt-1 text-xs text-mutedGray dark:text-darkTextSecondary">{hint}</p> : null}
    </article>
  );
}

function statusBadge(event: any) {
  if (event.status === 'confirmed') return 'Confirmed';
  if (event.needsReview) return 'AI Suggested · Needs Review';
  return 'AI Suggested';
}

function eta(eventTime: string | null) {
  if (!eventTime) return 'Time TBD';
  const diffMin = Math.round((new Date(eventTime).getTime() - Date.now()) / 60000);
  if (diffMin < 0) return `${Math.abs(diffMin)}m ago`;
  if (diffMin < 60) return `in ${diffMin}m`;
  return `in ${Math.round(diffMin / 60)}h`;
}

export default function Overview() {
  const macroFeed = useQuery({
    queryKey: ['admin', 'overview', 'macro-feed'],
    queryFn: async () => (await api.get('/api/macro/overview-feed?symbol=XAUUSD')).data,
  });

  const macroStatus = useQuery({
    queryKey: ['admin', 'overview', 'macro-status'],
    queryFn: async () => (await api.get('/api/macro/status?symbol=XAUUSD')).data,
  });

  const runtime = useQuery({
    queryKey: ['admin', 'overview', 'runtime'],
    queryFn: async () => (await api.get('/api/system/status')).data,
  });

  const strategies = useQuery({
    queryKey: ['admin', 'overview', 'strategies'],
    queryFn: async () => (await api.get('/api/strategies')).data as any[],
  });

  const subscriptions = useQuery({
    queryKey: ['admin', 'overview', 'subscriptions'],
    queryFn: async () => (await api.get('/api/subscriptions/overview')).data,
  });

  const accounts = useQuery({
    queryKey: ['admin', 'overview', 'accounts'],
    queryFn: async () => (await api.get('/api/trading-accounts')).data,
  });

  const highImpact = (macroFeed.data?.events || []).filter((event: any) => event.impactLevel === 'high');

  return (
    <div className="space-y-5">
      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {cardTitle('Upcoming High-Impact Macro Events', highImpact.length, 'Confirmed + AI suggestions')}
        {cardTitle('Current Macro Status', macroStatus.data?.summary || 'Unavailable', macroStatus.data?.severity || undefined)}
        {cardTitle('Active Runtime Status', runtime.data?.status || 'Unknown', runtime.data?.service || undefined)}
        {cardTitle('Strategies Monitored', strategies.data?.length || 0)}
        {cardTitle('Users Count', 'Pending', 'User metrics endpoint not wired yet')}
        {cardTitle('Active Subscriptions', subscriptions.data?.subscription?.status || 'Unknown')}
        {cardTitle('Trading Accounts Count', accounts.data?.items?.length || 0)}
      </section>

      <section className="panel p-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-base font-semibold text-darkText dark:text-darkTextPrimary">Upcoming High-Impact Macro Events</h3>
          <Link className="text-sm text-indigo-600" to="/macro-calendar">Open Macro Calendar</Link>
        </div>
        {!highImpact.length ? (
          <p className="text-sm text-mutedGray dark:text-darkTextSecondary">No high-impact events scheduled in the current lookahead window.</p>
        ) : (
          <div className="space-y-2">
            {highImpact.slice(0, 8).map((event: any) => (
              <div key={event.id} className="rounded-lg border border-gray-200 p-3 text-sm dark:border-darkCard">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="font-medium text-darkText dark:text-darkTextPrimary">{event.title}</div>
                  <span className="rounded bg-indigo-100 px-2 py-1 text-xs text-indigo-700">{statusBadge(event)}</span>
                </div>
                <div className="mt-1 text-mutedGray dark:text-darkTextSecondary">
                  {event.eventTime ? new Date(event.eventTime).toLocaleString() : 'Time TBD'} • {eta(event.eventTime)} • {event.currency} • {event.impactLevel}
                </div>
                <div className="mt-1 text-xs text-mutedGray dark:text-darkTextSecondary">
                  Source: {event.sourceType} • Live usage: {event.liveUsage}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
